import {
  REVIEW_MAX_PHOTOS,
  TRUST_PHOTO_MAX_BYTES,
  TRUST_PHOTO_TYPES,
  type MyReview,
  type PublicReview,
} from '@capsico/contracts';

/*
 * Pure helpers for the W17 rating screens: photo checks, the rate-form shape, fixed card heights
 * (so long review lists can be windowed) and a tiny local memory of an order's dishes (the API
 * sends the dish list only while an order waits to be rated; edits need the names again).
 */

export const MAX_PHOTOS = REVIEW_MAX_PHOTOS;

export type ReviewPhotoProblem = 'type' | 'size' | null;

export function reviewPhotoProblem(file: { type: string; size: number }): ReviewPhotoProblem {
  if (!(TRUST_PHOTO_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size <= 0 || file.size > TRUST_PHOTO_MAX_BYTES) return 'size';
  return null;
}

export interface RateForm {
  foodStars: number;
  deliveryStars: number;
  text: string;
  liked: string[];
  unliked: string[];
}

export const EMPTY_RATE_FORM: RateForm = {
  foodStars: 0,
  deliveryStars: 0,
  text: '',
  liked: [],
  unliked: [],
};

export function isEmptyRateForm(form: RateForm): boolean {
  return (
    form.foodStars === 0 &&
    form.deliveryStars === 0 &&
    form.text.trim() === '' &&
    form.liked.length === 0 &&
    form.unliked.length === 0
  );
}

export function formFromReview(review: MyReview): RateForm {
  return {
    foodStars: review.foodStars,
    deliveryStars: review.deliveryStars ?? 0,
    text: review.text ?? '',
    liked: [...review.likedDishIds],
    unliked: [...review.unlikedDishIds],
  };
}

/** Like → unlike → neither, keeping the two lists exclusive. */
export function cycleDish(
  form: RateForm,
  menuItemId: string,
  choice: 'like' | 'unlike',
): Pick<RateForm, 'liked' | 'unliked'> {
  const liked = form.liked.filter((id) => id !== menuItemId);
  const unliked = form.unliked.filter((id) => id !== menuItemId);
  const wasOn =
    choice === 'like' ? form.liked.includes(menuItemId) : form.unliked.includes(menuItemId);
  if (wasOn) return { liked, unliked };
  return choice === 'like'
    ? { liked: [...liked, menuItemId], unliked }
    : { liked, unliked: [...unliked, menuItemId] };
}

export function canStillEdit(review: MyReview, now: number = Date.now()): boolean {
  return review.status !== 'removed' && new Date(review.editableUntil).getTime() > now;
}

/* ---------------- fixed card heights (VirtualList) ---------------- */

const LINE = 20;
const CHARS_PER_LINE = 38;

/** Lines a review text takes (clamped to 3); the card clamps to the same number. */
export function textLines(text: string | null): number {
  if (!text) return 0;
  return Math.min(3, Math.max(1, Math.ceil(text.length / CHARS_PER_LINE)));
}

/** Public review card: header 44 + text + photos 72 + dishes 28 + reply 84 + padding + 12 gap. */
export function publicReviewHeight(review: PublicReview): number {
  let height = 32 + 44;
  const lines = textLines(review.text);
  if (lines > 0) height += 8 + lines * LINE;
  if (review.photos.length > 0) height += 72;
  if (review.likedDishes.length > 0) height += 28;
  if (review.reply) height += 84;
  return height + 12;
}

/** My review card: outlet header 44 + stars 28 + text + photos + status 28 + reply + actions. */
export function myReviewHeight(review: MyReview, editable: boolean): number {
  let height = 32 + 44 + 28;
  const lines = textLines(review.text);
  if (lines > 0) height += 8 + lines * LINE;
  if (review.photos.length > 0) height += 72;
  if (review.status !== 'published') height += 28;
  if (review.reply) height += 84;
  if (editable) height += 44;
  return height + 12;
}

/* ---------------- an order's dishes, remembered for edits ---------------- */

const DISHES_PREFIX = 'capsico:review-dishes:';
const PROMPTED_PREFIX = 'capsico:rate-prompted:';

export interface DishRef {
  menuItemId: string;
  name: string;
}

export function rememberDishes(orderId: string, dishes: DishRef[]): void {
  try {
    window.localStorage.setItem(DISHES_PREFIX + orderId, JSON.stringify(dishes));
  } catch {
    // Storage full or blocked: edits then show only the stars and text.
  }
}

export function recallDishes(orderId: string): DishRef[] {
  try {
    const raw = window.localStorage.getItem(DISHES_PREFIX + orderId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (entry): entry is DishRef =>
            typeof entry === 'object' &&
            entry !== null &&
            typeof (entry as DishRef).menuItemId === 'string' &&
            typeof (entry as DishRef).name === 'string',
        )
      : [];
  } catch {
    return [];
  }
}

/** The rate sheet opens by itself once per order (on the order page). */
export function wasPrompted(orderId: string): boolean {
  try {
    return window.localStorage.getItem(PROMPTED_PREFIX + orderId) === '1';
  } catch {
    return true;
  }
}

export function markPrompted(orderId: string): void {
  try {
    window.localStorage.setItem(PROMPTED_PREFIX + orderId, '1');
  } catch {
    // ignore
  }
}
